// Leads filters: the state, the options drawn from the loaded leads, and the
// predicate. Salesperson is the CRM assignment (lead_crm_profiles); Account
// manager is the Sheet column. The two are never mixed.

import { cleanCell, isPlaceholder } from '../../data/transform'
import type { Lead } from '../../types/crm'
import type { LeadAssignment, StaffMember } from './leadCrmClient'
import { getLeadDisplayName } from './leadDisplay'
import { getLeadEmail, getLeadPhone, hasSocialProfile } from './unifiedLeadFields'

export const ANY = ''
export const ALL_ASSIGNED = '__assigned'
export const UNASSIGNED = '__unassigned'

/** 'sheet' keeps the live Sheet's own row order; the others sort by display name. */
export type NameOrder = 'sheet' | 'az' | 'za'
export const NAME_ORDER_LABELS: Record<NameOrder, string> = { sheet: 'Sheet order', az: 'A → Z', za: 'Z → A' }

export type LeadFilterState = {
  /** '' any, ALL_ASSIGNED, UNASSIGNED, or a crm_staff id. */
  salesperson: string
  accountManager: string
  priorities: string[]
  stage: string
  state: string
  hasPhone: boolean
  hasEmail: boolean
  hasSocial: boolean
  nameOrder: NameOrder
}

export const EMPTY_FILTERS: LeadFilterState = {
  salesperson: ANY,
  accountManager: ANY,
  priorities: [],
  stage: ANY,
  state: ANY,
  hasPhone: false,
  hasEmail: false,
  hasSocial: false,
  nameOrder: 'sheet',
}

export type AssignmentLookup = {
  /** null while assignments are unknown; the salesperson filter is then ignored. */
  assignments: Record<string, LeadAssignment> | null
  keyOf: (lead: Lead) => string
}

const PRIORITY_ORDER = ['A+', 'A', 'B', 'C', 'D']

function usable(value: unknown): string {
  const text = cleanCell(value)
  return text && !isPlaceholder(text) && text !== '-' ? text : ''
}

export const accountManagerOf = (lead: Lead) => usable(lead.organization?.accountManager ?? lead.opportunity?.dealOwner)
export const stageOf = (lead: Lead) => usable(lead.opportunity?.stage)
export const stateOf = (lead: Lead) => usable(lead.organization?.state).toUpperCase()
const priorityOf = (lead: Lead) => usable(lead.priority)

function distinct(leads: Lead[], pick: (lead: Lead) => string, order?: string[]): string[] {
  const values = [...new Set(leads.map(pick).filter(Boolean))]
  const rank = (value: string) => {
    const index = order?.indexOf(value) ?? -1
    return index < 0 ? Number.MAX_SAFE_INTEGER : index
  }
  return values.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
}

export function filterOptions(leads: Lead[]) {
  return {
    accountManagers: distinct(leads, accountManagerOf),
    priorities: distinct(leads, priorityOf, PRIORITY_ORDER),
    stages: distinct(leads, stageOf),
    states: distinct(leads, stateOf),
  }
}

export function matchesFilters(lead: Lead, filters: LeadFilterState, lookup: AssignmentLookup): boolean {
  if (filters.salesperson !== ANY && lookup.assignments) {
    const key = lookup.keyOf(lead)
    const assignedTo = key ? lookup.assignments[key]?.assignedStaffId ?? null : null
    if (filters.salesperson === UNASSIGNED ? assignedTo !== null
      : filters.salesperson === ALL_ASSIGNED ? assignedTo === null
        : assignedTo !== filters.salesperson) return false
  }
  if (filters.accountManager && accountManagerOf(lead) !== filters.accountManager) return false
  if (filters.priorities.length && !filters.priorities.includes(priorityOf(lead))) return false
  if (filters.stage && stageOf(lead) !== filters.stage) return false
  if (filters.state && stateOf(lead) !== filters.state) return false
  if (filters.hasPhone && !getLeadPhone(lead)) return false
  if (filters.hasEmail && !getLeadEmail(lead)) return false
  if (filters.hasSocial && !hasSocialProfile(lead)) return false
  return true
}

// A lead's place in the live Sheet: its Organizations row when it has one,
// otherwise the order reconciliation gave it (Opportunities row, then
// Prospecting-only records after every Sheet-backed lead).
const sheetPosition = (lead: Lead) => lead.organization?.sheetRow ?? lead.order

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

/** Orders already-filtered leads. Stable, so equal keys keep Sheet order. */
export function sortLeads<TLead extends Lead>(leads: readonly TLead[], order: NameOrder): TLead[] {
  const bySheet = leads
    .map((lead, index) => ({ lead, index, row: sheetPosition(lead) }))
    .sort((a, b) => a.row - b.row || a.index - b.index)
  if (order !== 'az' && order !== 'za') return bySheet.map((entry) => entry.lead)
  const direction = order === 'az' ? 1 : -1
  return bySheet
    .map((entry, index) => ({ lead: entry.lead, index, name: getLeadDisplayName(entry.lead) }))
    .sort((a, b) => direction * collator.compare(a.name, b.name) || a.index - b.index)
    .map((entry) => entry.lead)
}

export type FilterChip = { id: string; label: string; clear: (filters: LeadFilterState) => LeadFilterState }

export function activeChips(filters: LeadFilterState, staff: StaffMember[]): FilterChip[] {
  const chips: FilterChip[] = []
  if (filters.salesperson) {
    const name = filters.salesperson === ALL_ASSIGNED ? 'Any assigned'
      : filters.salesperson === UNASSIGNED ? 'Unassigned'
        : staff.find((member) => member.id === filters.salesperson)?.displayName ?? 'Unknown'
    chips.push({ id: 'salesperson', label: `Salesperson: ${name}`, clear: (f) => ({ ...f, salesperson: ANY }) })
  }
  if (filters.accountManager) chips.push({ id: 'manager', label: `Account manager: ${filters.accountManager}`, clear: (f) => ({ ...f, accountManager: ANY }) })
  if (filters.priorities.length) chips.push({ id: 'priority', label: `Priority: ${filters.priorities.join(', ')}`, clear: (f) => ({ ...f, priorities: [] }) })
  if (filters.stage) chips.push({ id: 'stage', label: `Stage: ${filters.stage}`, clear: (f) => ({ ...f, stage: ANY }) })
  if (filters.state) chips.push({ id: 'state', label: `Location: ${filters.state}`, clear: (f) => ({ ...f, state: ANY }) })
  if (filters.hasPhone) chips.push({ id: 'phone', label: 'Has phone', clear: (f) => ({ ...f, hasPhone: false }) })
  if (filters.hasEmail) chips.push({ id: 'email', label: 'Has email', clear: (f) => ({ ...f, hasEmail: false }) })
  if (filters.hasSocial) chips.push({ id: 'social', label: 'Has social media', clear: (f) => ({ ...f, hasSocial: false }) })
  if (filters.nameOrder === 'az' || filters.nameOrder === 'za') {
    chips.push({ id: 'order', label: `Name order: ${NAME_ORDER_LABELS[filters.nameOrder]}`, clear: (f) => ({ ...f, nameOrder: 'sheet' }) })
  }
  return chips
}
